import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-[#F5F2EA]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-[#EBE9E1] sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link to="/">
            <Button variant="ghost" className="gap-2 text-[#0F5156]">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold text-[#0F5156] mb-2" style={{ fontFamily: 'Outfit' }}>
          Terms of Service
        </h1>
        <p className="text-slate-500 mb-8">Last updated: February 2025</p>

        <div className="prose prose-slate max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#1E293B] mb-4">1. Acceptance of Terms</h2>
            <p className="text-slate-600 mb-4">
              By accessing and using Strategic Architect ("the Service"), you accept and agree to be bound by the terms and provisions of this agreement. If you do not agree to these terms, please do not use the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#1E293B] mb-4">2. Description of Service</h2>
            <p className="text-slate-600 mb-4">
              Strategic Architect is an AI-powered strategic planning platform that provides tools for goal setting, task management, learning modules, and AI coaching. The Service includes both free and paid subscription tiers.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#1E293B] mb-4">3. User Accounts</h2>
            <p className="text-slate-600 mb-4">
              To use certain features of the Service, you must register for an account. You agree to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Provide accurate and complete information when creating your account</li>
              <li>Maintain the security of your password and account</li>
              <li>Accept responsibility for all activities that occur under your account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#1E293B] mb-4">4. Subscription and Payments</h2>
            <p className="text-slate-600 mb-4">
              The Service offers different subscription tiers (Free, Pro, and Enterprise). By subscribing to a paid plan, you agree to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Pay all fees associated with your selected subscription plan</li>
              <li>Provide accurate billing information</li>
              <li>Authorize us to charge your payment method on a recurring basis</li>
            </ul>
            <p className="text-slate-600 mt-4">
              Subscriptions automatically renew unless canceled before the renewal date. Refunds are handled on a case-by-case basis.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#1E293B] mb-4">5. Acceptable Use</h2>
            <p className="text-slate-600 mb-4">
              You agree not to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Use the Service for any illegal purpose</li>
              <li>Violate any laws, regulations, or third-party rights</li>
              <li>Attempt to gain unauthorized access to the Service</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Upload malicious code or content</li>
              <li>Impersonate another person or entity</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#1E293B] mb-4">6. Intellectual Property</h2>
            <p className="text-slate-600 mb-4">
              The Service and its original content, features, and functionality are owned by Strategic Architect and are protected by international copyright, trademark, and other intellectual property laws.
            </p>
            <p className="text-slate-600">
              You retain ownership of any content you create using the Service, but grant us a license to store and display that content as necessary to provide the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#1E293B] mb-4">7. AI-Generated Content</h2>
            <p className="text-slate-600 mb-4">
              The Service uses artificial intelligence to provide coaching, suggestions, and summaries. You acknowledge that:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>AI-generated content is for informational purposes only</li>
              <li>AI responses may not always be accurate or appropriate for your situation</li>
              <li>You should exercise your own judgment when acting on AI suggestions</li>
              <li>We are not liable for decisions made based on AI-generated content</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#1E293B] mb-4">8. Limitation of Liability</h2>
            <p className="text-slate-600 mb-4">
              To the maximum extent permitted by law, Strategic Architect shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#1E293B] mb-4">9. Termination</h2>
            <p className="text-slate-600 mb-4">
              We may terminate or suspend your account at any time, with or without cause, with or without notice. Upon termination, your right to use the Service will immediately cease.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#1E293B] mb-4">10. Changes to Terms</h2>
            <p className="text-slate-600 mb-4">
              We reserve the right to modify these terms at any time. We will notify users of significant changes via email or through the Service. Continued use of the Service after changes constitutes acceptance of the new terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#1E293B] mb-4">11. Contact Information</h2>
            <p className="text-slate-600">
              If you have any questions about these Terms of Service, please contact us at strategicarchitect@blueprintlifestrategies.com.
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#EBE9E1] bg-white/50 py-8">
        <div className="max-w-4xl mx-auto px-6 text-center text-slate-500 text-sm">
          © {new Date().getFullYear()} Strategic Architect. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
